import { Component, OnInit, Input } from '@angular/core';
import { FormGroup, FormControl, FormArray } from '@angular/forms';
import { AbstractAddComponent } from '../../../shared/abstracts/add/abstract-add/abstract-add.component';
import { IGoal, IPatient, ICodeableConcept, IAuditEvent, ICarePlan } from '@ahryman40k/ts-fhir-types/lib/R4';
import { ModalController, AlertController } from '@ionic/angular';
import { TranslatePipe } from '../../../shared/translate/pipes/translate.pipe';
import { dataToGoalAuditEventDetail } from '../../functions/goal.function';
import { Category } from '../../models/category.model';

@Component({
  selector: 'inc-goal-add',
  templateUrl: './goal-add.component.html',
  styleUrls: ['./goal-add.component.scss'],
})
export class GoalAddComponent extends AbstractAddComponent<{ data: IGoal, audit: IAuditEvent }[]> implements OnInit {
  @Input() patient: IPatient;
  @Input() isModal: boolean;
  @Input() user: any;
  @Input() careplan: ICarePlan;
  @Input() category: Category;

  careplanCode: ICodeableConcept;
  parameterCodes: ICodeableConcept[] = [];
  units: string[] = [];
  noMax: boolean[] = [];
  percent: boolean[] = [];
  lowerLimit: number[] = [];
  upperLimit: number[] = [];
  allSelect: boolean;

  index = 0;

  constructor(private modalController: ModalController, private alertController: AlertController, private translatePipe: TranslatePipe) {
    super();
  }

  ngOnInit() {
    this.setCategoryValues();
    this.addAllSelect();
    this.setFormValue();
  }

  setCategoryValues() {
    if (this.category) {
      this.careplanCode = this.category.carePlan.value;
      this.allSelect = this.category.carePlan.allSelect ? this.category.carePlan.allSelect.goal : false;

      Object.values(this.category.parameters).map(value => {
        this.parameterCodes.push(value.value);
        this.units.push(value.unit);
        this.noMax.push(value.noMax);
        this.percent.push(value.percent);
        this.lowerLimit.push(value.lowerLimit);
        this.upperLimit.push(value.upperLimit);
      });
    }
  }

  addAllSelect() {
    if (this.allSelect && !this.inData) {
      this.parameterCodes.unshift({ text: 'ALL' });
    }
  }

  setFormValue() {
    if (this.inData && this.inData[0] && this.inData[0].data) {
      this.form.patchValue(this.inData[0].data);

      if (this.parameterCodes) {
        this.form.patchValue({
          target: [{
            measure: this.parameterCodes.filter(value => value.text === this.inData[0].data.target[0].measure.text)[0]
          }]
        });
        this.index = this.parameterCodes.indexOf(this.form.value.target[0].measure);
      }
    } else if (this.parameterCodes) {
      this.form.patchValue({
        description: this.careplanCode,
        target: [{
          detailQuantity: {
            unit: this.units[0]
          },
          measure: this.parameterCodes[0]
        }],
        subject: {
          display: this.patient.name[0].text,
          reference: this.patient.id
        },
        expressedBy: {
          display: this.user.displayName,
          reference: this.user.uid
        }
      });
    }
  }

  resetForm() {
    return new FormGroup({
      id: new FormControl(),
      lifecycleStatus: new FormControl('active'),
      description: new FormControl(),
      target: new FormArray([
        new FormGroup({
          detailQuantity: new FormGroup({
            unit: new FormControl(''),
            value: new FormControl(''),
          }),
          dueDate: new FormControl(new Date().toISOString()),
          measure: new FormControl()
        })
      ]),
      subject: new FormGroup({
        reference: new FormControl(''),
        display: new FormControl('')
      }),
      expressedBy: new FormGroup({
        reference: new FormControl(''),
        display: new FormControl('')
      }),
      meta: new FormGroup({
        lastUpdated: new FormControl(new Date().toISOString())
      }),
      text: new FormGroup({
        div: new FormControl('')
      })
    });
  }

  close(data?: { data: IGoal, audit: IAuditEvent }[]) {
    if (this.isModal) {
      data ? this.modalController.dismiss(data) : this.modalController.dismiss();
    } else {
      this.save(data);
    }
  }

  add(data: IGoal) {
    const allData: { data: IGoal, audit: IAuditEvent }[] = [];

    if (this.allSelect && this.index === 0 && !this.inData) {
      for (let i = 1; i < this.parameterCodes.length; i++) {
        const dataClone: IGoal = JSON.parse(JSON.stringify(data));
        dataClone.description = this.careplanCode;
        dataClone.target[0].measure = this.parameterCodes[i];
        dataClone.meta.lastUpdated = new Date().toISOString();
        allData.push({ data: dataClone, audit: this.createAuditEvent(dataClone) });
      }
    } else {
      allData.push({ data, audit: this.createAuditEvent(data) });
    }
    this.close(allData);
  }

  selectChange(event: { detail: { value: ICodeableConcept } }) {
    this.index = this.parameterCodes.indexOf(event.detail.value);

    this.form.patchValue({
      description: this.careplanCode,
      target: [{
        detailQuantity: {
          unit: this.units[this.index],
          measure: this.parameterCodes[this.index]
        }
      }]
    });
  }

  checkPercentOrLimit(control: FormControl) {
    if (this.percent[this.index] && control.value > 100 ||
      control.value < this.lowerLimit[this.index] || control.value > this.upperLimit[this.index]) {
      control.setErrors({ error: 'value' });
    }
  }

  async openAlert(data?: IGoal) {
    const alert = await this.alertController.create({
      header: this.translatePipe.transform('EDIT_GOAL'),
      message: this.translatePipe.transform('THERAPY_CHANGE_Q'),
      buttons: [{
        text: this.translatePipe.transform('CANCEL_L'),
        role: 'cancel'
      }, {
        text: this.translatePipe.transform('YES'),
        handler: () => { this.add(data); }
      }]
    });
    await alert.present();
  }

  createAuditEvent(data: IGoal): IAuditEvent {
    const newAudit: IAuditEvent = {
      id: '',
      agent: [{
        name: this.patient.name[0].text,
        requestor: false,
        who: {
          reference: this.patient.id
        }
      }],
      entity: [{
        description: data.text ? data.text.div : '',
        detail: dataToGoalAuditEventDetail(this.inData && this.inData[0] && this.inData[0].data ? this.inData[0].data : null, data),
        what: {
          reference: this.careplan.id
        }
      }],
      recorded: new Date().toISOString(),
      source: {
        observer: {
          display: this.user.displayName,
          id: this.user.uid
        }
      },
      type: {
        display: 'goal'
      },
      resourceType: 'AuditEvent'
    };

    return newAudit;
  }
}
